import React, { useState, useRef } from "react";
import ApiService from "../ApiService";
import { Card, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import { parseAxiosError } from "../util/parserHelper";
import { formatFileSize } from "../util/common";
import { BsReplyFill, BsXCircle } from "react-icons/bs";
import { AttachFile } from "@material-ui/icons";

const MAX_ATTACHMENTS = 5;

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const ReplyMessage = props => {
  const [messageUUID] = useState(props.messageUUID);
  const [loading, setLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackText, setSnackText] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("");

  const [selectedAttachment, setSelectedAttachment] = useState([]);
  const [attachedFileS3URLs, setAttachedFileS3URLs] = useState([]);

  const fileInputRef = useRef(null);

  const handleSnackOpen = (text, severity) => {
    setSnackSeverity(severity);
    setSnackText(text);
    setSnackOpen(true);
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const handleAttachmentClick = () => {
    // Mirror the disabled visual state: don't open the picker mid-upload or mid-send.
    if (fileUploading || loading) return;
    if (selectedAttachment.length >= MAX_ATTACHMENTS) {
      return handleSnackOpen(`Only ${MAX_ATTACHMENTS} files allowed at a time`, "error");
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async event => {
    const picked = Array.from(event.target.files || []);
    if (picked.length === 0) return;

    // Cap at MAX_ATTACHMENTS total across already-uploaded + newly picked
    const remaining = MAX_ATTACHMENTS - selectedAttachment.length;
    if (remaining <= 0) {
      handleSnackOpen(`Only ${MAX_ATTACHMENTS} files allowed at a time`, "error");
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }
    const toUpload = picked.slice(0, remaining);
    const cappedSelection = picked.length > remaining;

    setFileUploading(true);
    // React 18 batches synchronous setState; surface only one toast here so the cap warning
    // (when present) isn't overwritten by a generic "Uploading file" before the user can read it.
    if (cappedSelection) {
      handleSnackOpen(`Only ${MAX_ATTACHMENTS} files allowed; uploading the first ${remaining}`, "warning");
    } else {
      handleSnackOpen("Uploading file", "info");
    }

    try {
      for (const file of toUpload) {
        try {
          const resp = await ApiService.uploadFile(file, "MESSAGE_ATTACHMENTS");
          const { url } = resp?.data?.body || {};
          if (!url) {
            handleSnackOpen("Upload failed: missing url", "error");
            continue;
          }
          // Mutate File so name/size/type survive (spread on File loses prototype getters).
          file.url = url;
          setSelectedAttachment(prev => [...prev, file]);
          setAttachedFileS3URLs(prev => [...prev, url]);
          handleSnackOpen(`Uploaded ${file.name}`, "success");
        } catch (err) {
          handleSnackOpen(`Upload failed: ${parseAxiosError(err)?.message || "unknown"}`, "error");
        }
      }
    } finally {
      setFileUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };

  const removeFileFromAttachment = async index => {
    const target = selectedAttachment[index];
    const fileUrl = target?.url;
    if (!fileUrl) return;

    // Optimistic update: drop the URL from local state BEFORE awaiting the delete network call.
    // This closes a race where the user could click Send between the remove click and the
    // deleteFile response — handleSubmit would otherwise read a stale attachedFileS3URLs and
    // include a URL that's about to disappear from S3, leaving a broken link for the patient.
    // We filter both arrays by url (not by index) so overlapping removals stay consistent.
    setSelectedAttachment(prev => prev.filter(item => item?.url !== fileUrl));
    setAttachedFileS3URLs(prev => prev.filter(u => u !== fileUrl));
    if (fileInputRef.current) fileInputRef.current.value = null;

    try {
      await ApiService.deleteFile("MESSAGE_ATTACHMENTS", fileUrl);
      handleSnackOpen("Attachment removed", "success");
    } catch (error) {
      // Roll back so the user can retry; preserve original ordering.
      setSelectedAttachment(prev => {
        if (prev.some(item => item?.url === fileUrl)) return prev;
        const restored = [...prev];
        restored.splice(Math.min(index, restored.length), 0, target);
        return restored;
      });
      setAttachedFileS3URLs(prev => (prev.includes(fileUrl) ? prev : [...prev, fileUrl]));
      handleSnackOpen(parseAxiosError(error)?.message || "Failed to remove attachment", "error");
    }
  };

  const handleSubmit = event => {
    const form = event.currentTarget;
    event.preventDefault();

    const content = document.getElementById("content").value.trim();
    if (!content) {
      alert("Reply cannot be empty");
      return;
    }

    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      setLoading(true);
      async function postMessageReply() {
        try {
          // eslint-disable-next-line no-unused-vars
          const postMessageReply = await ApiService.postMessageReply({
            originalMessageUUID: messageUUID,
            content: content,
            attachmentUrls: attachedFileS3URLs
          })
            .then(resp => {
              if (window.location.pathname === "/MessageDetails" && resp?.data) {
                window.location.replace(`${process.env.REACT_APP_BASE_URL}/MessageDetails?messageId=${resp.data}`);
              } else {
                window.location.reload();
              }
            })
            .catch(error => {
              const msg = error?.response?.data?.message ?? error?.message;
              console.error(error);
              alert(msg);
            });
        } catch (err) {
          const msg = err?.response?.data?.message ?? err?.message;
          console.error(err);
          alert(msg);
        } finally {
          setLoading(false);
        }
      }
      postMessageReply();
    }
  };


  return (
    <Card body className="replyFullBody">
      <Card.Body className="replyDetailBody">
        <Card.Title className="replytitle" as={Row}>
          <Col><BsReplyFill /></Col>
          <Col className="text-right"><BsXCircle /></Col>
        </Card.Title>
        <hr />
        <Form className="needs-validation new_msg_form" id="msgSendForm" noValidate onSubmit={handleSubmit}>
          <textarea name="body" id="content" cols="30" rows="10" className="msg_text_input msg_inputs send_textarea"></textarea>

          {selectedAttachment.length > 0 && (
            <div
              className="attachment"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                marginTop: "10px"
              }}
            >
              {selectedAttachment.map((item, index) => (
                <div
                  key={`${item?.url || item?.name}-${index}`}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    maxWidth: "100%",
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    background: "#f5f5f5",
                    color: "#333",
                    fontWeight: "bold",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.1)"
                  }}
                >
                  <div
                    style={{
                      color: "#b29ff1",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "180px"
                    }}
                    title={item?.name}
                  >
                    {item?.name}
                  </div>
                  <div style={{ margin: "0px 10px", fontWeight: "normal" }}>{formatFileSize(item?.size || 0)}</div>
                  <button
                    type="button"
                    aria-label={`Remove ${item?.name || "attachment"}`}
                    onClick={() => removeFileFromAttachment(index)}
                    disabled={fileUploading || loading}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: fileUploading || loading ? "not-allowed" : "pointer",
                      color: "#666"
                    }}
                  >
                    <i className="fa fa-times" aria-hidden="true" style={{ margin: "0px 6px" }}></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          <hr />
          <Row
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              rowGap: "8px"
            }}
          >
            <Button
              id="msg_reply"
              type="submit"
              className="replyButton sendButton"
              disabled={loading || fileUploading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Send"}
            </Button>
            {(() => {
              const attachDisabled = fileUploading || loading;
              return (
                <>
                  <div
                    onClick={handleAttachmentClick}
                    role="button"
                    tabIndex={0}
                    aria-label="Attach file"
                    aria-disabled={attachDisabled}
                    onKeyDown={e => {
                      if ((e.key === "Enter" || e.key === " ") && !attachDisabled) {
                        e.preventDefault();
                        handleAttachmentClick();
                      }
                    }}
                    style={{
                      cursor: attachDisabled ? "not-allowed" : "pointer",
                      opacity: attachDisabled ? 0.5 : 1,
                      display: "inline-flex",
                      alignItems: "center"
                    }}
                  >
                    {fileUploading ? (
                      <Spinner animation="border" size="sm" style={{ marginLeft: "10px" }} />
                    ) : (
                      <AttachFile style={{ color: "black", marginLeft: "10px" }} />
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    multiple
                    disabled={attachDisabled}
                  />
                </>
              );
            })()}
          </Row>
        </Form>
      </Card.Body>
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity || "info"}>
          {snackText}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default ReplyMessage;
